/**
 * The app's story clock, ported from home-ceo-mobile
 * `src/components/home/wee-timeline.ts` (named `eee-timeline.ts` at main
 * @ 8d85fd1).
 *
 * The WEE section and the water behind it are authored against this
 * clock, so the web keeps the app's cues, cycle and easings unchanged.
 * Every ambient rate is a whole number of turns per cycle (a multiple
 * of OMEGA), which makes the picture periodic in exactly one cycle: the
 * wrap from `loop` back to `wish` lands on the phase it left, so
 * nothing jumps at the seam.
 */

/** Cue times in seconds. Welcome plays once; after that the clock cycles Wish → Enact. */
export const CUE = {
  welcome: 0,
  wish: 3.2,
  explore: 8.8,
  enact: 15.4,
  loop: 22.0,
} as const;

/** Length of one Wish → Explore → Enact cycle, in seconds. */
export const CYCLE = CUE.loop - CUE.wish;

/** One turn per cycle, in radians per second. */
export const OMEGA = (2 * Math.PI) / CYCLE;

/** The moment inside Explore when the pick is made. */
export const SELECTION_AT = CUE.explore + 3.9;

/** The moment inside Enact when the chosen house comes alive. */
export const ALIVE_AT = CUE.enact + 2.5;

export type Beat = 'welcome' | 'wish' | 'explore' | 'enact';

/** The three beats that loop, in the order the story tells them. */
export const LOOP_BEATS = ['wish', 'explore', 'enact'] as const;
export type LoopBeat = (typeof LOOP_BEATS)[number];

/** Where each looping beat starts and ends on the clock. */
export const BEAT_WINDOW: Record<LoopBeat, { from: number; to: number }> = {
  wish: { from: CUE.wish, to: CUE.explore },
  explore: { from: CUE.explore, to: CUE.enact },
  enact: { from: CUE.enact, to: CUE.loop },
};

/**
 * Fold an unbounded elapsed time onto the authored axis: the first pass
 * runs from 0 straight through, every pass after that cycles from
 * `wish`.
 */
export function wrap(elapsed: number): number {
  if (elapsed < CUE.loop) return elapsed;
  return CUE.wish + ((elapsed - CUE.wish) % CYCLE);
}

/** Which beat `t` falls in. */
export function beatAt(t: number): Beat {
  if (t < CUE.wish) return 'welcome';
  if (t < CUE.explore) return 'wish';
  if (t < CUE.enact) return 'explore';
  return 'enact';
}

export function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

/** Normalised progress across [start, end], clamped at both ends. */
function progress(t: number, start: number, end: number): number {
  if (end <= start) return t >= end ? 1 : 0;
  return clamp01((t - start) / (end - start));
}

/**
 * easeInOutCubic from `from` to `to` across [start, end], clamped
 * outside it. The workhorse: holds fall out for free.
 */
export function glide(t: number, from: number, to: number, start: number, end: number): number {
  const p = progress(t, start, end);
  const e = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
  return from + (to - from) * e;
}

/** easeOutCubic from 0 to 1. Things arriving. */
export function enter(t: number, start: number, dur = 0.8): number {
  const p = progress(t, start, start + dur);
  return 1 - Math.pow(1 - p, 3);
}

/**
 * easeOutBack from 0 to 1: overshoots and settles. Reserved, as in the
 * app, for the moments that are decisions rather than transitions.
 */
export function pop(t: number, start: number, dur = 0.7): number {
  const p = progress(t, start, start + dur);
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(p - 1, 3) + c1 * Math.pow(p - 1, 2);
}

/** Rises to 1 at the midpoint of a window and falls back to 0. */
export function bell(p: number): number {
  return 4 * p * (1 - p);
}

/**
 * Deterministic pseudo-random in [0, 1) from two integers — the app's
 * hash, so the scatter lands where it does on the phone. Evaluated here
 * in double precision, never in a shader: float32 `sin` of arguments in
 * the thousands differs between GPUs and would re-roll the scatter.
 */
export function seeded(i: number, j: number): number {
  const x = Math.sin(i * 127.1 + j * 311.7) * 43758.5453;
  return x - Math.floor(x);
}
